import dayjs from "@punchup/utils/dayjs";

/**
 * Format a number for display with K/M abbreviations for large numbers
 * Examples:
 * - 999 -> "999"
 * - 1000 -> "1K"
 * - 1500 -> "1.5K"
 * - 1000000 -> "1M"
 */
export function formatClapCount(count: number): string {
  if (count <= 9999) {
    return count.toLocaleString();
  } else if (count < 1000000) {
    const formattedCount = (count / 1000).toFixed(1);
    // Remove trailing .0
    return formattedCount.endsWith(".0")
      ? formattedCount.slice(0, -2) + "K"
      : formattedCount + "K";
  } else {
    const formattedCount = (count / 1000000).toFixed(1);
    // Remove trailing .0
    return formattedCount.endsWith(".0")
      ? formattedCount.slice(0, -2) + "M"
      : formattedCount + "M";
  }
}

/**
 * Format a special's "published at" data for human readable display in past tense
 */
export function formatSpecialAge(publishedAt: string) {
  const publishedDate = dayjs(publishedAt);
  const now = dayjs();

  const seconds = now.diff(publishedDate, "second");
  const minutes = now.diff(publishedDate, "minute");
  const hours = now.diff(publishedDate, "hour");
  const days = now.diff(publishedDate, "day");
  const weeks = now.diff(publishedDate, "week");
  const months = now.diff(publishedDate, "month");
  const years = now.diff(publishedDate, "year");

  if (seconds < 60) {
    return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;
  } else if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (days < 7) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (days < 30) {
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  } else if (months < 12) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else {
    return years === 1 ? "1 year ago" : `${years} years ago`;
  }
}

/**
 * Format a number with locale-appropriate separators
 * Examples:
 * - 1000 -> "1,000"
 * - 1000000 -> "1,000,000"
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Whether a date falls in the current year *as read in the zone it will be
 * rendered in*. Comparing in the runtime's own zone instead lets a show a few
 * hours either side of New Year print a year the rendered date contradicts.
 *
 * Both sides go through `format` rather than `isSame(…, "year")` so the rule is
 * decided by the very call that renders the date.
 */
export const isCurrentYearIn = (date: string, timezone: string) =>
  dayjs.utc(date).tz(timezone).format("YYYY") ===
  dayjs().tz(timezone).format("YYYY");

/**
 * Table-width version of `formatDate`, carrying the same rule: the year only
 * appears when it is not the current one. Passing an explicit format to
 * `formatDate` opts out of that rule, which is how one table ended up printing
 * "09/22/2026" beside a modal printing "Sep 22".
 *
 * - this year  -> "Sep 22, 7:30 PM"
 * - other year -> "Sep 22, 2025, 7:30 PM"
 */
export const formatDateCompact = (date: string, timezone: string) =>
  formatDate(
    date,
    timezone,
    isCurrentYearIn(date, timezone) ? "MMM D, h:mm A" : "MMM D, YYYY, h:mm A"
  );

export const formatDate = (date: string, timezone: string, format?: string) => {
  if (format) {
    return dayjs.utc(date).tz(timezone).format(format);
  }
  const defaultFormat = isCurrentYearIn(date, timezone)
    ? "dddd, MMMM D [at] h:mma"
    : "dddd, MMMM D, YYYY [at] h:mma";
  return dayjs.utc(date).tz(timezone).format(defaultFormat);
};
